import { useNotification } from 'admin';
import { Country, EncordFolderEnum, useApiQualityExternalUploadV1Create } from 'api';
import { formatDateToDateTimeIsoString } from 'date';
import {
  CountryAutocompleteField,
  DateField,
  Form,
  SelectField,
  SiteMaterialAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { Box, Button, Grid } from 'ui';

const MODEL_OPTIONS = Object.values(EncordFolderEnum).map((model) => ({
  label: model,
  value: model,
}));

const schema = yup.object().shape({
  country: yup.string().oneOf(Object.values(Country)).optional(),
  encord_folder: yup.string().oneOf(Object.values(EncordFolderEnum)).required(),
  sample_id: yup.string().optional(),
  site_description: yup.string().optional(),
  site_material_id: yup.number().optional(),
  taken_at: yup.string().required(),
  trade_company_id: yup.number().required(),
});

type FormValues = yup.InferType<typeof schema>;

export const ExternalEvidenceForm = ({ onSubmitSuccess }: { onSubmitSuccess: (id: string) => void }) => {
  const { open } = useNotification();
  const form = useForm({
    resolver: yupResolver(schema),
  });

  const tradeCompanyId = form.watch('trade_company_id');

  const { mutate, isPending, isSuccess } = useApiQualityExternalUploadV1Create({
    mutation: {
      onError: () => {
        open?.({
          message: 'Error creating external evidence',
          type: 'error',
        });
      },
      onSuccess: (data) => {
        open?.({
          description: 'You can now upload evidence',
          message: 'External evidence created',
          type: 'success',
        });
        onSubmitSuccess(data.uuid);
      },
    },
  });
  const onSubmit = (data: FormValues) => {
    mutate({
      data: {
        ...data,
        taken_at: formatDateToDateTimeIsoString(data.taken_at),
      },
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit}>
      <Grid container>
        <Grid item xs={12} md={4}>
          <SelectField label="Encord Folder" name="encord_folder" options={MODEL_OPTIONS} required={true} />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateField label="Taken At" name="taken_at" fullWidth required={true} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TradeCompanyAutocomplete name="trade_company_id" label="Trade Company" required={true} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SiteMaterialAutocomplete
            name="site_material_id"
            label="Site Material"
            tradeCompanyId={tradeCompanyId}
            required={false}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CountryAutocompleteField name="country" label="Country" required={false} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField multiline label="Site Description" name="site_description" required={false} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField label="Sample ID" name="sample_id" required={false} />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button type="submit" loading={isPending} disabled={isSuccess}>
              Confirm
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};
